import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssignmentTurnedInOutlined,
  CheckCircleOutline,
  CloseOutlined,
  DeleteOutline,
  EditNoteOutlined,
  EditOutlined,
  PictureAsPdfOutlined,
  VisibilityOutlined,
  AssignmentOutlined,
  FileDownloadOutlined,
  ChevronRight,
  ExpandMore,
  TableView
} from "@mui/icons-material";
import { Alert, Box, Chip, FormControlLabel, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import DataTable from "../../../components/common/tables/DataTable";
import ApprovalTimeline from "../components/ApprovalTimeline";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import { AppButton, AppSplitButton } from "components/common/buttons";
import ApprovalWorkflowDialogs, { ReviewNoteField } from "./ApprovalWorkflowDialogs";
import { getAllUsers } from "../../systems/services/user.service";
import { subcontractApi } from "../services/subcontractApi";
import exportSubcontractAssignmentForm from "../utils/subcontracts/exportSubcontractAssignmentForm";
import {
  buildItemHierarchy,
  collectExpandableItemIds,
  flattenVisibleItemTree,
  getItemCodeLevel,
  getItemHierarchyKey,
  hasChildItems,
  sortItemsByCode,
} from "../utils/subcontract-items/itemHierarchy";
import {
  PRIORITIES,
  STATUS_COLOR_BY_VALUE,
  STATUS_COLORS,
  normalizeStatusValue,
} from "constants/projectRules";
import { calculateAmount } from "modules/contracts/utils/commercialCalculations";
import { formatAmount, formatDate } from "utils/formatters";

const normalize = (value) => String(value || "").trim().toUpperCase();
const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("vi-VN")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0111/g, "d")
    .replace(/đ/g, "d");
const isApprovedStatus = (value) => {
  const status = normalizeStatusKey(value);
  return status.includes("da phe duyet") || status.includes("duoc phe duyet");
};
const isPendingApprovalStatus = (value) => {
  const status = normalizeStatusKey(value);
  return status.includes("cho") && status.includes("duyet");
};
const isEditingApprovalStatus = (value) => {
  const status = normalizeStatusKey(value);
  return status.includes("chinh sua") || status.includes("dang sua") || status.includes("yeu cau sua");
};
const getStatusChipStyle = (value) => {
  const directStyle = STATUS_COLOR_BY_VALUE[normalizeStatusValue(value)];
  if (directStyle) return directStyle;

  const status = normalizeStatusKey(value);
  if (status.includes("da phe duyet") || status.includes("duoc phe duyet")) return STATUS_COLORS.success;
  if (status.includes("dang xu ly")) return STATUS_COLORS.info;
  if (status.includes("cho xu ly") || (status.includes("cho") && status.includes("duyet"))) return STATUS_COLORS.warning;
  if (status.includes("chinh sua") || status.includes("yeu cau sua")) return STATUS_COLORS.purple;
  return STATUS_COLORS.default;
};
const getId = (value, key) => value?.[key] ?? value?.[`${key[0].toUpperCase()}${key.slice(1)}`];
const getProfileId = (profile) => profile?.approvalProfileId ?? profile?.ApprovalProfileId;
const itemIdOf = (item) => item?.itemId ?? item?.ItemId;
const draftItemIdOf = (item) => item?.drafItemId ?? item?.DrafItemId;
const approvalItemIdOf = (item) => item?.approvalItemId ?? item?.ApprovalItemId;
const getScopeId = (value) => value?.subcontractItemScopeId ?? value?.SubcontractItemScopeId ?? value?.scopeId;
const APPROVAL_STEPS = [
  { order: 1, field: "step1ApproverUserId", name: "Trợ lý KHTH", label: "Cấp 1 - Trợ lý KHTH" },
  { order: 2, field: "step2ApproverUserId", name: "Trưởng phòng KHTH", label: "Cấp 2 - Trưởng phòng KHTH" },
  { order: 3, field: "step3ApproverUserId", name: "Trưởng phòng Tài chính", label: "Cấp 3 - Trưởng phòng Tài chính" },
  { order: 4, field: "step4ApproverUserId", name: "Ban giám đốc", label: "Cấp 4 - Ban giám đốc" },
];
const buildApprovalStepsPayload = (form) =>
  APPROVAL_STEPS.map((step) => ({
    stepOrder: step.order,
    stepName: step.name,
    approverUserId: Number(form[step.field]),
  }));
// const money = (value) => new Intl.NumberFormat("vi-VN").format(Number(value || 0));
const formatQuantity = (value) =>
  new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(value || 0));
const priorityLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return "Cao";
    case 2:
      return "Trung bình";
    case 3:
      return "Thấp";
    default:
      return PRIORITIES.find((item) => Number(item.value) === Number(value))?.label || "";
  }
};
const getDifficultyColor = (level) => {
  switch (Number(level)) {
    case 1:
      return "#4caf50";
    case 2:
      return "#8bc34a";
    case 3:
      return "#ff9800";
    case 4:
      return "#f44336";
    case 5:
      return "#9c27b0";
    default:
      return "#9e9e9e";
  }
};
const getPriorityColor = (value) => {
  switch (Number(value)) {
    case 1:
      return "#f44336";
    case 2:
      return "#ff9800";
    case 3:
      return "#4caf50";
    default:
      return "#9e9e9e";
  }
};
const userLabel = (user) => {
  if (!user) return "";
  return user.fullName || user.FullName || user.userName || user.UserName || `Người dùng #${user.userId ?? user.UserId}`;
};
const sortScopeGroups = (groups = []) => [...groups].sort((left, right) =>
  Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0)
  || String(left.label || "").localeCompare(String(right.label || ""), "vi", { sensitivity: "base" }),
);
const getScopeLabel = (item) => {
  const scopeType = normalize(item.scopeType || item.ScopeType);
  const scopeName = item.scopeName || item.ScopeName;
  const provinceName = item.provinceName || item.ProvinceName;
  const communeName = item.communeName || item.CommuneName;

  if (scopeType === "PROVINCE") return provinceName || scopeName || "Tỉnh/Thành phố";
  if (scopeType === "COMMUNE") return communeName || scopeName || "Xã/Phường";
  if (scopeType === "TOTAL") return scopeName || "Tổng hợp";
  if (scopeType === "ESTIMATE") return scopeName || "Dự toán";
  return scopeName || "Chưa xác định phân cấp";
};
const getScopeSubtitle = (item) => {
  const sectionName = item.sectionName || item.SectionName;
  const scopeType = normalize(item.scopeType || item.ScopeType);

  if (scopeType === "ESTIMATE") return "";
  return sectionName || "";
};
const displayValue = (value) => value === null || value === undefined || value === "" ? "-" : value;
const nullableNumber = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);
const getTeamOptions = (options = []) =>
  options.map((option) => ({
    value: option.personnelId ?? option.PersonnelId,
    label: option.label ?? option.Label ?? option.fullName ?? option.FullName ?? "",
  })).filter((option) => option.value != null);
const linesFromValue = (value) => Array.isArray(value) ? value : [];
const getNextDraftCode = (items = [], parentItem = null) => {
  if (parentItem?.itemCode) {
    const prefix = `${parentItem.itemCode}.`;
    const nextChildIndex = items
      .filter((item) => String(item.itemCode || "").startsWith(prefix) && getItemCodeLevel(item.itemCode) === getItemCodeLevel(parentItem.itemCode) + 1)
      .map((item) => Number(String(item.itemCode || "").slice(prefix.length).split(".")[0]))
      .filter(Number.isFinite)
      .reduce((max, value) => Math.max(max, value), 0) + 1;
    return `${parentItem.itemCode}.${nextChildIndex}`;
  }

  const nextRootIndex = items
    .filter((item) => !item.drafParentItemId && !item.DrafParentItemId && !item.parentItemId && !item.ParentItemId && getItemCodeLevel(item.itemCode ?? item.ItemCode) <= 1)
    .map((item) => Number(item.itemCode ?? item.ItemCode))
    .filter(Number.isFinite)
    .reduce((max, value) => Math.max(max, value), 0) + 1;
  return String(nextRootIndex);
};

export default function ApprovalWorkflowView({
  subcontractId,
  subcontract = {},
  approvals = [],
  items = [],
  officialItems = [],
  sections = [],
  onChanged,
  mode = "view",
  currentUser = null,
}) {
  const canSubmit = mode === "edit";
  const canApprove = mode === "approve";
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [approvalInfoDialog, setApprovalInfoDialog] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewRows, setReviewRows] = useState([]);
  const reviewNoteRef = useRef("");
  const [reviewNoteResetKey, setReviewNoteResetKey] = useState(0);
  const [reviewError, setReviewError] = useState("");
  const [draftDialog, setDraftDialog] = useState(null);
  const [draftNotice, setDraftNotice] = useState(null);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [decisionForm, setDecisionForm] = useState(null);
  const [decisionError, setDecisionError] = useState("");
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionDownloadFormat, setDecisionDownloadFormat] = useState(null);
  const [assignmentFormExporting, setAssignmentFormExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState({ open: false, message: "", severity: "info" });
  const [showOfficialCompare, setShowOfficialCompare] = useState(false);
  const [expandedItemIds, setExpandedItemIds] = useState(new Set());

  const handleToggleExpand = useCallback((item) => {
    const itemKey = getItemHierarchyKey(item);
    setExpandedItemIds((current) => {
      const next = new Set(current);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  }, []);

  const updateReviewNoteRef = useCallback((value) => {
    reviewNoteRef.current = value;
  }, []);

  const resetReviewNote = useCallback(() => {
    reviewNoteRef.current = "";
    setReviewNoteResetKey((current) => current + 1);
  }, []);

  const userById = useMemo(
    () => new Map(users.map((user) => [String(user.userId ?? user.UserId), user])),
    [users],
  );
  const itemById = useMemo(
    () => new Map(items.map((item) => [String(itemIdOf(item)), item])),
    [items],
  );
  const officialItemById = useMemo(
    () => new Map(officialItems.map((item) => [String(itemIdOf(item)), item])),
    [officialItems],
  );
  const scopeById = useMemo(() => {
    const map = new Map();
    sections.forEach((section) => {
      (section.scopes || []).forEach((scope) => {
        const scopeId = getScopeId(scope);
        if (!scopeId) return;
        map.set(String(scopeId), {
          ...scope,
          sectionId: getId(section, "subcontractSectionId"),
          sectionName: section.sectionName || section.name || section.SectionName,
          sectionType: section.sectionType || section.SectionType,
          sectionDisplayOrder: section.displayOrder ?? section.DisplayOrder ?? 0,
          displayOrder: scope.displayOrder ?? scope.DisplayOrder ?? 0,
        });
      });
    });
    return map;
  }, [sections]);

  const hydrateApprovalItem = useMemo(
    () => (approvalItem, options = {}) => {
      const sourceItem = options?.preferOfficial
        ? officialItemById.get(String(itemIdOf(approvalItem))) || itemById.get(String(itemIdOf(approvalItem)))
        : itemById.get(String(itemIdOf(approvalItem))) || officialItemById.get(String(itemIdOf(approvalItem)));
      const scopeId = getScopeId(sourceItem) || getScopeId(approvalItem);
      const scope = scopeId ? scopeById.get(String(scopeId)) : null;

      return {
        ...(sourceItem || {}),
        ...approvalItem,
        itemId: itemIdOf(approvalItem) ?? itemIdOf(sourceItem),
        itemCode: sourceItem?.itemCode ?? sourceItem?.ItemCode ?? approvalItem?.itemCode ?? approvalItem?.ItemCode ?? "",
        itemName: sourceItem?.itemName ?? sourceItem?.ItemName ?? approvalItem?.itemName ?? approvalItem?.ItemName ?? "",
        unit: sourceItem?.unit ?? sourceItem?.Unit ?? approvalItem?.unit ?? approvalItem?.Unit ?? "",
        unitPrice: sourceItem?.unitPrice ?? sourceItem?.UnitPrice ?? approvalItem?.unitPrice ?? approvalItem?.UnitPrice ?? 0,
        subcontractQuantity:
          sourceItem?.subcontractQuantity ?? sourceItem?.SubcontractQuantity ?? approvalItem?.subcontractQuantity ?? approvalItem?.SubcontractQuantity ?? 0,
        subcontractAmount:
          sourceItem?.subcontractAmount ?? sourceItem?.SubcontractAmount ?? approvalItem?.subcontractAmount ?? approvalItem?.SubcontractAmount ?? 0,
        difficultyLevel:
          sourceItem?.difficultyLevel ?? sourceItem?.DifficultyLevel ?? approvalItem?.difficultyLevel ?? approvalItem?.DifficultyLevel ?? "",
        priority: sourceItem?.priority ?? sourceItem?.Priority ?? approvalItem?.priority ?? approvalItem?.Priority ?? "",
        status: sourceItem?.status ?? sourceItem?.Status ?? approvalItem?.status ?? approvalItem?.Status ?? "",
        parentItemId: sourceItem?.parentItemId ?? sourceItem?.ParentItemId ?? approvalItem?.parentItemId ?? approvalItem?.ParentItemId ?? null,
        parentItemCode: sourceItem?.parentItemCode ?? sourceItem?.ParentItemCode ?? approvalItem?.parentItemCode ?? approvalItem?.ParentItemCode ?? null,
        drafParentItemId: sourceItem?.drafParentItemId ?? sourceItem?.DrafParentItemId ?? approvalItem?.drafParentItemId ?? approvalItem?.DrafParentItemId ?? null,
        hasChildren: sourceItem?.hasChildren ?? sourceItem?.HasChildren ?? approvalItem?.hasChildren ?? approvalItem?.HasChildren ?? false,
        subcontractItemScopeId: scopeId || null,
        scopeId: scopeId || null,
        scopeName: sourceItem?.scopeName || sourceItem?.ScopeName || approvalItem?.scopeName || approvalItem?.ScopeName || scope?.scopeName || scope?.ScopeName || scope?.name,
        scopeType: sourceItem?.scopeType || sourceItem?.ScopeType || approvalItem?.scopeType || approvalItem?.ScopeType || scope?.scopeType || scope?.ScopeType,
        sectionId: sourceItem?.subcontractSectionId || sourceItem?.SubcontractSectionId || approvalItem?.subcontractSectionId || approvalItem?.SubcontractSectionId || scope?.sectionId || "",
        sectionName: sourceItem?.sectionName || sourceItem?.SectionName || approvalItem?.sectionName || approvalItem?.SectionName || scope?.sectionName,
        sectionType: sourceItem?.sectionType || sourceItem?.SectionType || approvalItem?.sectionType || approvalItem?.SectionType || scope?.sectionType,
        provinceId: sourceItem?.provinceId || sourceItem?.ProvinceId || approvalItem?.provinceId || approvalItem?.ProvinceId || scope?.provinceId || scope?.ProvinceId,
        provinceName: sourceItem?.provinceName || sourceItem?.ProvinceName || approvalItem?.provinceName || approvalItem?.ProvinceName || scope?.provinceName || scope?.ProvinceName,
        communeId: sourceItem?.communeId || sourceItem?.CommuneId || approvalItem?.communeId || approvalItem?.CommuneId || scope?.communeId || scope?.CommuneId,
        communeName: sourceItem?.communeName || sourceItem?.CommuneName || approvalItem?.communeName || approvalItem?.CommuneName || scope?.communeName || scope?.CommuneName,
        sectionDisplayOrder: scope?.sectionDisplayOrder ?? 0,
        scopeDisplayOrder: scope?.displayOrder ?? 0,
      };
    },
    [itemById, officialItemById, scopeById],
  );

  const approvalProfiles = useMemo(
    () => approvals.map((profile) => ({
      ...profile,
      items: (profile.items || []).map(hydrateApprovalItem),
    })),
    [approvals, hydrateApprovalItem],
  );
  const getApprovalSteps = useCallback((profile) => profile?.steps || profile?.Steps || [], []);
  const inlineReviewProfile = useMemo(() => {
    const sortedProfiles = [...approvalProfiles].sort((left, right) =>
      Number(right.submitCount ?? right.SubmitCount ?? 0) - Number(left.submitCount ?? left.SubmitCount ?? 0)
      || new Date(right.submittedAt ?? right.SubmittedAt ?? right.createdAt ?? right.CreatedAt ?? 0).getTime()
      - new Date(left.submittedAt ?? left.SubmittedAt ?? left.createdAt ?? left.CreatedAt ?? 0).getTime()
    );

    return sortedProfiles.find((profile) => isPendingApprovalStatus(profile.status ?? profile.Status)) || sortedProfiles[0] || null;
  }, [approvalProfiles]);
  const isEditingCurrentApproval = isEditingApprovalStatus(inlineReviewProfile?.status ?? inlineReviewProfile?.Status);
  const approvalActionLabel = isEditingCurrentApproval ? "Chỉnh sửa phê duyệt" : "Tạo phê duyệt";
  const approvalDialogTitle = isEditingCurrentApproval ? "Chỉnh sửa nội dung phê duyệt" : "Khởi tạo phê duyệt";
  const canEditApprovalInfo = canSubmit && isEditingCurrentApproval;
  const getStepByOrder = useCallback((profile, stepOrder) =>
    getApprovalSteps(profile).find((step) => Number(step.stepOrder ?? step.StepOrder) === Number(stepOrder)),
    [getApprovalSteps],
  );
  const approvalSubmitInitialForm = useMemo(() => {
    if (!isEditingCurrentApproval || !inlineReviewProfile) return null;

    return {
      approvalName: inlineReviewProfile.approvalName ?? inlineReviewProfile.ApprovalName ?? "",
      approverUserId: getStepByOrder(inlineReviewProfile, 1)?.approverUserId ?? getStepByOrder(inlineReviewProfile, 1)?.ApproverUserId ?? "",
      step1ApproverUserId: getStepByOrder(inlineReviewProfile, 1)?.approverUserId ?? getStepByOrder(inlineReviewProfile, 1)?.ApproverUserId ?? "",
      step2ApproverUserId: getStepByOrder(inlineReviewProfile, 2)?.approverUserId ?? getStepByOrder(inlineReviewProfile, 2)?.ApproverUserId ?? "",
      step3ApproverUserId: getStepByOrder(inlineReviewProfile, 3)?.approverUserId ?? getStepByOrder(inlineReviewProfile, 3)?.ApproverUserId ?? "",
      step4ApproverUserId: getStepByOrder(inlineReviewProfile, 4)?.approverUserId ?? getStepByOrder(inlineReviewProfile, 4)?.ApproverUserId ?? "",
      overallComment: inlineReviewProfile.overallComment ?? inlineReviewProfile.OverallComment ?? "",
    };
  }, [getStepByOrder, inlineReviewProfile, isEditingCurrentApproval]);
  const editableApprovalProfileId = getProfileId(inlineReviewProfile);
  const approvalInfoInitialData = useMemo(() => {
    if (!approvalInfoDialog) return {};

    const step1 = getStepByOrder(approvalInfoDialog, 1);
    const step2 = getStepByOrder(approvalInfoDialog, 2);
    const step3 = getStepByOrder(approvalInfoDialog, 3);
    const step4 = getStepByOrder(approvalInfoDialog, 4);

    return {
      approvalName: approvalInfoDialog.approvalName ?? approvalInfoDialog.ApprovalName ?? "",
      step1ApproverUserId: step1?.approverUserId ?? step1?.ApproverUserId ?? "",
      step2ApproverUserId: step2?.approverUserId ?? step2?.ApproverUserId ?? "",
      step3ApproverUserId: step3?.approverUserId ?? step3?.ApproverUserId ?? "",
      step4ApproverUserId: step4?.approverUserId ?? step4?.ApproverUserId ?? "",
      overallComment: approvalInfoDialog.overallComment ?? approvalInfoDialog.OverallComment ?? "",
    };
  }, [approvalInfoDialog, getStepByOrder]);
  const groupApprovalItemsByScope = useCallback((approvalItems = []) => {
    const groups = new Map();

    sortItemsByCode(approvalItems.map(hydrateApprovalItem)).forEach((item) => {
      const key = item.subcontractItemScopeId ? `scope-${item.subcontractItemScopeId}` : "scope-unknown";
      const current = groups.get(key) || {
        key,
        scopeId: item.subcontractItemScopeId || item.scopeId,
        label: getScopeLabel(item),
        subtitle: getScopeSubtitle(item),
        displayOrder: Number(item.sectionDisplayOrder || 0) * 1000 + Number(item.scopeDisplayOrder || 0),
        items: [],
      };
      current.items.push(item);
      groups.set(key, current);
    });

    return sortScopeGroups([...groups.values()]);
  }, [hydrateApprovalItem]);

  const getVisibleItemRows = useCallback(
    (rows = []) => flattenVisibleItemTree(buildItemHierarchy(rows), expandedItemIds),
    [expandedItemIds],
  );

  const getExpandableItemKeys = useCallback(
    (rows = []) => collectExpandableItemIds(buildItemHierarchy(rows)),
    [],
  );

  const handleExpandItemRows = useCallback((rows = []) => {
    const expandableItemKeys = getExpandableItemKeys(rows);
    setExpandedItemIds((current) => {
      const next = new Set(current);
      expandableItemKeys.forEach((itemKey) => next.add(itemKey));
      return next;
    });
  }, [getExpandableItemKeys]);

  const handleCollapseItemRows = useCallback((rows = []) => {
    const expandableItemKeys = new Set(getExpandableItemKeys(rows));
    setExpandedItemIds((current) => {
      const next = new Set(current);
      expandableItemKeys.forEach((itemKey) => next.delete(itemKey));
      return next;
    });
  }, [getExpandableItemKeys]);

  useEffect(() => {
    const nextExpandedItemIds = new Set();
    const addExpandableRows = (rows = []) => {
      getExpandableItemKeys(rows).forEach((itemKey) => {
        nextExpandedItemIds.add(itemKey);
      });
    };

    approvalProfiles.forEach((profile) => {
      groupApprovalItemsByScope(profile.items || []).forEach((group) => addExpandableRows(group.items || []));
    });
    groupApprovalItemsByScope(reviewRows).forEach((group) => addExpandableRows(group.items || []));

    setExpandedItemIds(nextExpandedItemIds);
  }, [approvalProfiles, getExpandableItemKeys, groupApprovalItemsByScope, reviewRows]);

  const approvalStepChips = useCallback((profile) => {
    const steps = getApprovalSteps(profile);
    if (!steps.length) return null;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {steps.map((step, index) => {
          const user = userLabel(userById.get(String(step.approverUserId ?? step.ApproverUserId))) || step.fullName || step.FullName || "-";
          const status = step.status ?? step.Status ?? "Chờ xử lý";
          const style = getStatusChipStyle(status);
          const stepName = step.stepName ?? step.StepName ?? `Cấp ${index + 1}`;

          return (
            <Tooltip key={`${step.stepOrder ?? step.StepOrder ?? index}-${user}`} title={`${stepName}: ${status}`}>
              <Chip
                label={user}
                size="small"
                variant="outlined"
                sx={{
                  height: 25,
                  maxWidth: 180,
                  fontWeight: 600,
                  fontSize: 10,
                  color: style.color,
                  backgroundColor: style.background,
                  borderColor: style.border,
                  "& .MuiChip-label": {
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    );
  }, [getApprovalSteps, userById]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "approvalName", header: "Hồ sơ phê duyệt", size: 200,
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
      },
      {
        accessorKey: "submittedAt", header: "Ngày gửi", size: 80,
        Cell: ({ cell }) => cell.getValue() ? formatDate(cell.getValue()) : "-",
      },
      {
        accessorKey: "submittedBy", header: "Người gửi", size: 120,
        Cell: ({ row, cell }) => {
          const user = cell.getValue() ?? userLabel(userById.get(String(row.original.submittedUserId ?? row.original.SubmittedUserId)));
          if (!user) return "-";

          return (
            <Chip
              label={user}
              size="small"
              variant="outlined"
              sx={{
                height: 25,
                maxWidth: 180,
                fontWeight: 600,
                fontSize: 10,
                "& .MuiChip-label": {
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: "approver", header: "Người phê duyệt", size: 120,
        Cell: ({ row, cell }) => {
          const chips = approvalStepChips(row.original);
          if (chips) return chips;

          const user = cell.getValue() || userLabel(userById.get(String(row.original.approverUserId ?? row.original.ApproverUserId)));
          if (!user) return "-";

          const style = getStatusChipStyle(row.original.status ?? row.original.Status);
          return (
            <Chip
              label={user}
              size="small"
              variant="outlined"
              sx={{
                height: 25,
                maxWidth: 180,
                fontWeight: 600,
                fontSize: 10,
                color: style.color,
                backgroundColor: style.background,
                borderColor: style.border,
                "& .MuiChip-label": {
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: "respondedAt", header: "Ngày phản hồi", size: 80,
        Cell: ({ cell }) => cell.getValue() ? formatDate(cell.getValue()) : "-"
      },
      {
        accessorKey: "status", header: "Trạng thái", size: 90,
        Cell: ({ cell }) => <StatusChip value={cell.getValue()} />
      },
      {
        accessorKey: "overallComment", header: "Nhận xét", size: 200,
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
      },
    ],
    [approvalStepChips, userById],
  );

  const itemDetailColumns = useMemo(() => {
    const hideForParent = (renderer) => (props) =>
      hasChildItems(props.row?.original) ? null : renderer(props);

    return [
      {
        accessorKey: "itemCode",
        header: "STT",
        size: 55,
        // minSize: 100,
        maxSize: 55,
        Cell: ({ cell, row }) => {
          const item = row.original;
          const level = item.level_ || 0;

          return (
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: 24,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {hasChildItems(item) && !item._isOfficialCompare ? (
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleToggleExpand(item);
                    }}
                  >
                    {expandedItemIds.has(getItemHierarchyKey(item)) ? (
                      <ExpandMore sx={{ fontSize: "0.9rem" }} />
                    ) : (
                      <ChevronRight sx={{ fontSize: "0.9rem" }} />
                    )}
                  </IconButton>
                ) : null}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "11px",
                  fontWeight: level === 0 ? 600 : 400,
                }}
              >
                {cell.getValue() || ""}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "itemName",
        header: "Dự toán",
        size: 300,
        minSize: 170,
        maxSize: 350,
        muiTableBodyCellProps: () => ({ align: "left" }),
        Cell: ({ cell, row }) => {
          const item = row.original;
          const level = item.level_ || 0;

          return (
            <Typography
              variant="body2"
              sx={{
                pl: `${Math.min(level, 4) * 14}px`,
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
                fontWeight: level === 0 ? 600 : 400,
              }}
            >
              {cell.getValue() || "-"}
            </Typography>
          )
        }
      },
      {
        accessorKey: "unit", header: "ĐVT", size: 40,
        Cell: hideForParent(({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
              }}
            >
              {cell.getValue() || ""}
            </Typography>
          );
        }),
      },
      {
        accessorKey: "difficultyLevel", header: "Mức KK", size: 50,
        Cell: hideForParent(({ cell }) => {
          const level = cell.getValue();
          if (level === null || level === undefined || level === "" || Number(level) === 0) return null;

          return (
            <Chip
              label={String(level)}
              size="small"
              variant="outlined"
              sx={{
                borderColor: getDifficultyColor(level),
                color: getDifficultyColor(level),
                fontFamily: "Inter, sans-serif",
                fontSize: "0.525rem",
                height: "20px",
                "& .MuiChip-label": {
                  padding: "0 6px",
                },
              }}
            />
          );
        }),
      },
      {
        accessorKey: "laborCount", header: "Định biên", size: 60,
        Cell: hideForParent(({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
              }}
            >
              {cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === ""
                ? ""
                : formatQuantity(cell.getValue())}
            </Typography>
          );
        }),
      },
      {
        accessorKey: "laborNorm", header: "Định mức", size: 60,
        Cell: hideForParent(({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
              }}
            >
              {cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === ""
                ? ""
                : formatQuantity(cell.getValue())}
            </Typography>
          );
        }),
      },
      {
        accessorKey: "laborPrice", header: "Đơn giá công", size: 70,
        Cell: hideForParent(({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
              }}
            >
              {formatAmount(cell.getValue())}
            </Typography>
          );
        }),
      },
      {
        accessorKey: "unitPrice", header: "Đơn giá sản phẩm", size: 70,
        Cell: hideForParent(({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
              }}
            >
              {formatAmount(cell.getValue())}
            </Typography>
          );
        }),
      },
      {
        accessorKey: "subcontractQuantity", header: "Khối lượng", size: 50,
        Cell: hideForParent(({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
              }}
            >
              {cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === ""
                ? ""
                : formatQuantity(cell.getValue())}
            </Typography>
          );
        }),
      },
      {
        accessorKey: "subcontractAmount", header: "Thành tiền", size: 110,
        muiTableBodyCellProps: () => ({ align: "right" }),
        Cell: ({ cell, row }) => {
          const item = row.original;
          const level = item.level_ || 0;

          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
                fontWeight: level === 0 ? 600 : 400,
                color: level === 0 ? "#D36135" : "inherit"
              }}
            >
              {formatAmount(cell.getValue())}
            </Typography>
          );
        },
      },
      {
        accessorKey: "priority", header: "Độ ưu tiên", size: 80,
        Cell: hideForParent(({ cell }) => {
          const priority = Number(cell.getValue());
          if (!priority) return null;

          return (
            <Chip
              label={priorityLabel(priority)}
              size="small"
              variant="outlined"
              sx={{
                borderColor: getPriorityColor(priority),
                color: getPriorityColor(priority),
                "& .MuiChip-label": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.58rem",
                  fontWeight: 600,
                },
              }}
            />
          );
        }),
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 130,
        Cell: hideForParent(({ cell }) => cell.getValue() ? <StatusChip value={cell.getValue()} /> : null),
      },
    ];
  }, [expandedItemIds, handleToggleExpand]);

  const compareFields = useMemo(
    () => new Set(itemDetailColumns.map((column) => column.accessorKey).filter(Boolean)),
    [itemDetailColumns],
  );

  const normalizeCompareValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") return Number(value.toFixed(4));
    if (!Number.isNaN(Number(value)) && String(value).trim() !== "") return Number(Number(value).toFixed(4));
    return String(value).trim();
  };

  const getCompareValue = (row, field) => {
    if (!row || !field) return "";
    return row[field] ?? row[`${field[0].toUpperCase()}${field.slice(1)}`] ?? "";
  };

  const isOfficialCompareDifferent = (row, field) => {
    if (!row?._isOfficialCompare || !row?._draftCompareSource || !compareFields.has(field)) return false;
    return normalizeCompareValue(getCompareValue(row, field)) !==
      normalizeCompareValue(getCompareValue(row._draftCompareSource, field));
  };

  const withCompareCellProps = (column) => ({
    ...column,
    muiTableBodyCellProps: (props) => {
      const baseProps = typeof column.muiTableBodyCellProps === "function"
        ? column.muiTableBodyCellProps(props)
        : column.muiTableBodyCellProps || {};
      const field = column.accessorKey;
      const changed = isOfficialCompareDifferent(props.row?.original, field);
      return {
        ...baseProps,
        sx: {
          ...(baseProps.sx || {}),
          ...(props.row?.original?._isOfficialCompare
            ? { bgcolor: changed ? "#fff7cc" : "#f8fafc", color: "#475569" }
            : {}),
        },
      };
    },
  });

  const detailColumns = useMemo(() => [
    ...itemDetailColumns,
    {
      accessorKey: "reviewStatus",
      header: "Kết quả phê duyệt",
      size: 120,
      Cell: ({ cell }) => <StatusChip value={cell.getValue()} />,
    },
    {
      accessorKey: "comment",
      header: "Nhận xét",
      size: 220,
      muiTableBodyCellProps: () => ({ align: "left" }),
      Cell: ({ cell }) => cell.getValue() || "-",
    },
  ], [itemDetailColumns]);

  const reviewItemGroups = groupApprovalItemsByScope(reviewRows);
  const buildCompareRows = useCallback((rows = []) => {
    const visibleRows = getVisibleItemRows(rows);

    if (!showOfficialCompare) return visibleRows;

    return visibleRows.flatMap((row) => {
      const official = officialItemById.get(String(itemIdOf(row)));
      const officialRow = official
        ? hydrateApprovalItem({
          ...official,
          approvalItemId: `${approvalItemIdOf(row)}-official`,
          reviewStatus: "Bản chính",
          comment: "",
        }, { preferOfficial: true })
        : {
          ...row,
          approvalItemId: `${approvalItemIdOf(row)}-official-missing`,
          itemName: "Chưa có trong bản chính",
          reviewStatus: "Bản chính",
          comment: "",
        };

      return [
        { ...row, _compareRole: "draft" },
        {
          ...officialRow,
          level_: row.level_,
          _compareRole: "official",
          _isOfficialCompare: true,
          _draftCompareSource: row,
        },
      ];
    });
  }, [getVisibleItemRows, hydrateApprovalItem, officialItemById, showOfficialCompare]);
  const sortedReviewDisplayRows = useMemo(
    () => buildCompareRows(reviewRows),
    [buildCompareRows, reviewRows],
  );
  const reviewDisplayItemGroups = useMemo(
    () => reviewItemGroups.map((group) => ({
      ...group,
      items: buildCompareRows(group.items || []),
    })),
    [buildCompareRows, reviewItemGroups],
  );
  const draftDialogScopeId = draftDialog?.scopeId ?? getScopeId(draftDialog?.data);
  const activeDraftScopeItems = useMemo(
    () => items.filter((item) => String(getScopeId(item)) === String(draftDialogScopeId)),
    [draftDialogScopeId, items],
  );
  const draftParentOptions = useMemo(
    () => sortItemsByCode(activeDraftScopeItems)
      .filter((item) => getItemCodeLevel(item.itemCode ?? item.ItemCode) < 4)
      .map((item) => ({
        value: draftItemIdOf(item),
        label: `${item.itemCode || item.ItemCode || ""} - ${item.itemName || item.ItemName || ""}${(item.difficultyLevel ?? item.DifficultyLevel) == null ? "" : ` (KK ${item.difficultyLevel ?? item.DifficultyLevel})`}`,
      }))
      .filter((item) => item.value != null),
    [activeDraftScopeItems],
  );

  const userOptions = useMemo(() => {
    const teamPersonnelIds = new Set(
      teamMembers
        .filter((member) => (member.isActive ?? member.IsActive) !== false)
        .map((member) => Number(member.personnelId ?? member.PersonnelId ?? member.userId ?? member.UserId))
        .filter(Number.isFinite),
    );

    return users
      .filter((user) => teamPersonnelIds.has(Number(user.personnelId ?? user.PersonnelId)))
      .map((user) => ({
        value: user.userId ?? user.UserId,
        userId: user.userId ?? user.UserId,
        personnelId: user.personnelId ?? user.PersonnelId,
        label: userLabel(user),
      }))
      .filter((item) => item.value != null);
  }, [teamMembers, users]);
  const approvalInfoFields = useMemo(() => [
    { name: "approvalName", label: "Tiêu đề phê duyệt", full: true },
    ...APPROVAL_STEPS.map((step) => ({
      name: step.field,
      label: step.label,
      options: userOptions,
      autocomplete: true,
      required: true,
      validate: step.order === APPROVAL_STEPS.length
        ? (value, form) => {
          const approverIds = APPROVAL_STEPS.map((item) =>
            Number(item.field === step.field ? value : form[item.field])
          );
          return new Set(approverIds).size === approverIds.length ? "" : "Vui lòng chọn 4 người phê duyệt khác nhau.";
        }
        : undefined,
    })),
    { name: "overallComment", label: "Nội dung", multiline: true, minRows: 3, full: true },
  ], [userOptions]);
  const decisionTeamOptions = useMemo(
    () => getTeamOptions(decisionForm?.teamOptions || decisionForm?.TeamOptions || []),
    [decisionForm],
  );
  const assignmentDecisionStatus =
    reviewDialog?.status ??
    reviewDialog?.Status ??
    inlineReviewProfile?.status ??
    inlineReviewProfile?.Status ??
    subcontract?.approvalStatus ??
    subcontract?.ApprovalStatus;
  const canCreateAssignmentDecision = isApprovedStatus(assignmentDecisionStatus);
  const showExportNotice = (message, severity = "info") => {
    setExportNotice({ open: true, message, severity });
  };

  useEffect(() => {
    getAllUsers()
      .then((result) => setUsers(result.data || []))
      .catch(() => setUsers([]));
    subcontractApi.getTeam(subcontractId)
      .then((result) => setTeamMembers(result || []))
      .catch(() => setTeamMembers([]));
  }, [subcontractId]);

  useEffect(() => {
    if (!canApprove || !inlineReviewProfile) return;

    setReviewDialog(inlineReviewProfile);
    setReviewRows((inlineReviewProfile.items || []).map((item) => ({
      ...hydrateApprovalItem(item),
      nextStatus: item.reviewStatus || "Chờ phê duyệt",
      nextComment: item.comment || "",
    })));
    resetReviewNote();
    setReviewError("");
  }, [
    canApprove,
    hydrateApprovalItem,
    inlineReviewProfile,
    inlineReviewProfile?.approvalProfileId,
    inlineReviewProfile?.ApprovalProfileId,
    resetReviewNote,
  ]);

  const openAssignmentDecisionDialog = async () => {
    if (!canCreateAssignmentDecision) {
      showExportNotice(`Chưa thể export. Trạng thái hồ sơ hiện tại: ${assignmentDecisionStatus || "Không xác định"}.`, "warning");
      return;
    }

    showExportNotice("Đang tải dữ liệu quyết định giao khoán...", "info");
    setDecisionDialogOpen(true);
    setDecisionLoading(true);
    setDecisionError("");
    try {
      const defaults = await subcontractApi.getAssignmentDecisionDefaults(subcontractId);
      setDecisionForm({
        ...defaults,
        issueDate: defaults.issueDate || defaults.IssueDate || new Date().toISOString(),
        legalBasis: defaults.legalBasis || defaults.LegalBasis || [],
        recipients: defaults.recipients || defaults.Recipients || [],
        productionDirectorIds: defaults.selectedProductionDirectorIds || defaults.SelectedProductionDirectorIds || [],
        planningDepartmentIds: defaults.selectedPlanningDepartmentIds || defaults.SelectedPlanningDepartmentIds || [],
        teamOptions: defaults.teamOptions || defaults.TeamOptions || [],
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Không thể tải dữ liệu quyết định giao khoán.";
      setDecisionError(message);
      showExportNotice(message, "error");
    } finally {
      setDecisionLoading(false);
    }
  };

  const downloadAssignmentDecision = async (format, formData = decisionForm) => {
    if (!formData) return;
    if (!String(formData.documentNumber || "").trim()) {
      setDecisionError("Vui lòng nhập số văn bản.");
      return;
    }
    if (!String(formData.place || "").trim()) {
      setDecisionError("Vui lòng nhập địa danh.");
      return;
    }

    setDecisionDownloadFormat(format);
    setDecisionError("");
    try {
      const payload = {
        documentNumber: formData.documentNumber || "",
        documentSymbol: formData.documentSymbol || "QĐ-TĐ",
        place: formData.place || "",
        issueDate: formData.issueDate || new Date().toISOString(),
        summary: formData.summary || "",
        authority: formData.authority || "",
        legalBasis: linesFromValue(formData.legalBasis),
        productionDirectorIds: formData.productionDirectorIds || [],
        planningDepartmentIds: formData.planningDepartmentIds || [],
        productionDirectorText: formData.productionDirectorText || "",
        planningDepartmentText: formData.planningDepartmentText || "",
        productionUnitText: formData.productionUnitText || "",
        workContent: formData.workContent || "",
        technicalRequirements: formData.technicalRequirements || "",
        fundingGuarantee: formData.fundingGuarantee || "",
        article2: formData.article2 || "",
        recipients: linesFromValue(formData.recipients),
        signatureTitle: formData.signatureTitle || "GIÁM ĐỐC",
        directorName: formData.directorName || "",
      };
      if (format === "word") {
        await subcontractApi.downloadAssignmentDecisionWord(subcontractId, payload);
      } else {
        await subcontractApi.downloadAssignmentDecisionPdf(subcontractId, payload);
      }
    } catch (error) {
      const fileType = format === "word" ? "Word" : "PDF";
      setDecisionError(error?.response?.data?.message || error?.message || `Không thể tải file ${fileType}.`);
    } finally {
      setDecisionDownloadFormat(null);
    }
  };

  const downloadAssignmentDecisionPdf = (formData = decisionForm) =>
    downloadAssignmentDecision("pdf", formData);

  const downloadAssignmentDecisionWord = (formData = decisionForm) =>
    downloadAssignmentDecision("word", formData);

  const downloadAssignmentFormExcel = async () => {
    if (!canCreateAssignmentDecision) {
      showExportNotice(`Chưa thể export. Trạng thái hồ sơ hiện tại: ${assignmentDecisionStatus || "Không xác định"}.`, "warning");
      return;
    }

    setAssignmentFormExporting(true);
    setReviewError("");
    showExportNotice("Đang xuất phiếu giao khoán...", "info");
    try {
      await exportSubcontractAssignmentForm({
        sections,
        subcontract,
        items: officialItems,
        exportedBy: currentUser?.fullName || currentUser?.FullName || currentUser?.userName || currentUser?.UserName || "",
      });
      showExportNotice("Đã xuất phiếu giao khoán.", "success");
    } catch (error) {
      const message = error?.message || "Không thể xuất phiếu giao khoán.";
      setReviewError(message);
      showExportNotice(message, "error");
    } finally {
      setAssignmentFormExporting(false);
    }
  };

  const saveApprovalInfo = async (formData) => {
    if (!approvalInfoDialog) return;

    const approvalProfileId = getProfileId(approvalInfoDialog);
    const steps = buildApprovalStepsPayload(formData);
    const isEditingApprovalInfo = isEditingApprovalStatus(approvalInfoDialog.status ?? approvalInfoDialog.Status);
    const currentStepOrder = isEditingApprovalInfo ? 1 : Number(
      approvalInfoDialog.revisionRequestedByStepOrder ??
      approvalInfoDialog.RevisionRequestedByStepOrder ??
      approvalInfoDialog.currentStepOrder ??
      approvalInfoDialog.CurrentStepOrder ??
      1
    );
    const currentApproverId =
      steps.find((step) => step.stepOrder === currentStepOrder)?.approverUserId ??
      steps[0].approverUserId;

    await subcontractApi.updateApprovalProfile(approvalProfileId, {
      approvalProfileId,
      subcontractId: Number(subcontractId),
      approvalName: formData.approvalName || "Hồ sơ phê duyệt",
      approverUserId: currentApproverId,
      status: approvalInfoDialog.status ?? approvalInfoDialog.Status ?? null,
      overallComment: formData.overallComment || null,
      steps,
    });

    setApprovalInfoDialog(null);
    await onChanged?.();
  };

  const submitApproval = async ({ form, submitItems }) => {
    if (!canSubmit) return;
    const itemIds = submitItems.map((item) => itemIdOf(item)).filter(Boolean);

    setSubmitting(true);
    try {
      const profile = await subcontractApi.createApprovalProfile({
        subcontractId: Number(subcontractId),
        approvalName: form.approvalName || "Hồ sơ phê duyệt",
        submittedAt: new Date().toISOString(),
        submitCount: approvals.length + 1,
        approverUserId: Number(form.approverUserId),
        steps: buildApprovalStepsPayload(form),
        status: "Chờ phê duyệt",
        overallComment: form.overallComment || null,
      });

      const approvalProfileId = profile.approvalProfileId ?? profile.ApprovalProfileId;
      await Promise.all(itemIds.map((itemId) => subcontractApi.createApprovalItem({
        approvalProfileId,
        itemId,
        reviewStatus: "Chờ phê duyệt",
      })));

      setOpen(false);
      await onChanged?.();
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || "Không thể gửi hồ sơ phê duyệt.");
    } finally {
      setSubmitting(false);
    }
  };

  const saveDraftItem = async (formData) => {
    const scopeId = draftDialogScopeId;
    if (!scopeId) return;
    const targetScopeOptions = draftDialog?.scopeOptions?.length
      ? draftDialog.scopeOptions
      : [{ value: scopeId }];

    const parentInActiveScope = formData.drafParentItemId
      ? activeDraftScopeItems.find((item) => String(draftItemIdOf(item)) === String(formData.drafParentItemId))
      : null;
    const itemCode = formData.itemCode || getNextDraftCode(activeDraftScopeItems, parentInActiveScope);
    const quantity = formData.subcontractQuantity === "" || formData.subcontractQuantity === null || formData.subcontractQuantity === undefined
      ? 0
      : Number(formData.subcontractQuantity);
    const unitPrice = nullableNumber(formData.unitPrice);
    const basePayload = {
      ...formData,
      subcontractId: Number(subcontractId),
      itemCode,
      drafParentItemId: formData.drafParentItemId || null,
      parentItemId: parentInActiveScope?.sourceItemId || itemIdOf(parentInActiveScope) || null,
      subcontractQuantity: quantity,
      unitPrice,
      subcontractAmount: calculateAmount(quantity, unitPrice),
      laborCount: nullableNumber(formData.laborCount),
      laborNorm: nullableNumber(formData.laborNorm),
      laborPrice: nullableNumber(formData.laborPrice),
      priority: formData.priority === "" ? null : Number(formData.priority),
      difficultyLevel: formData.difficultyLevel === "" ? null : Number(formData.difficultyLevel),
      level_: Math.max(getItemCodeLevel(itemCode) - 1, 0),
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
    };

    try {
      setDraftNotice(null);
      if (draftDialog?.data) {
        await subcontractApi.updateDraftItem(draftItemIdOf(draftDialog.data), {
          ...basePayload,
          subcontractItemScopeId: Number(scopeId),
        });
      } else {
        await subcontractApi.createDraftItemBatch({
          ...basePayload,
          scopes: targetScopeOptions.map((scope) => {
            const scopeItems = items.filter((item) => String(getScopeId(item)) === String(scope.value));
            const matchingParent = parentInActiveScope
              ? scopeItems.find((item) =>
                String(item.itemCode || item.ItemCode) === String(parentInActiveScope.itemCode || parentInActiveScope.ItemCode) &&
                String(item.difficultyLevel ?? item.DifficultyLevel ?? "") === String(parentInActiveScope.difficultyLevel ?? parentInActiveScope.DifficultyLevel ?? ""),
              )
              : null;
            return {
              subcontractItemScopeId: Number(scope.value),
              drafParentItemId: matchingParent ? draftItemIdOf(matchingParent) : null,
              parentItemId: matchingParent?.sourceItemId || itemIdOf(matchingParent) || null,
              quantityMultiplier: 1,
            };
          }),
        });
      }

      setDraftDialog(null);
      await onChanged?.();
      setDraftNotice({ severity: "success", message: "Lưu dự toán bản nháp thành công" });
    } catch (error) {
      setDraftNotice({
        severity: "error",
        message:
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          error?.message ||
          "Không thể lưu dự toán bản nháp",
      });
      throw error;
    }
  };

  const deleteDraftItem = async (item) => {
    const drafItemId = draftItemIdOf(item);
    if (!drafItemId) return;

    try {
      setDraftNotice(null);
      const response = await subcontractApi.deleteDraftItem(drafItemId);
      if (response?.success === false || response?.Success === false) {
        setDraftNotice({
          severity: "error",
          message: response?.message || response?.Message || "Không thể xóa dự toán bản nháp",
        });
        return;
      }

      await onChanged?.();
      setDraftNotice({
        severity: "success",
        message: response?.message || response?.Message || "Xóa dự toán bản nháp thành công",
      });
    } catch (error) {
      setDraftNotice({
        severity: "error",
        message:
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          error?.message ||
          "Không thể xóa dự toán bản nháp",
      });
    }
  };

  const openReviewDialog = (profile) => {
    setReviewDialog(profile);
    setReviewRows((profile.items || []).map((item) => ({
      ...hydrateApprovalItem(item),
      nextStatus: item.reviewStatus || "Chờ phê duyệt",
      nextComment: item.comment || "",
    })));
    resetReviewNote();
    setReviewError("");
  };

  const markReviewItem = (approvalItemId, nextStatus) => {
    setReviewRows((current) => current.map((item) =>
      approvalItemIdOf(item) === approvalItemId ? { ...item, nextStatus } : item
    ));
  };

  const updateReviewComment = (approvalItemId, nextComment) => {
    setReviewRows((current) => current.map((item) =>
      approvalItemIdOf(item) === approvalItemId ? { ...item, nextComment } : item
    ));
  };

  const saveReview = async (mode) => {
    if (!canApprove || !reviewDialog) return;
    const reviewNote = reviewNoteRef.current;
    const approvalProfileId = reviewDialog.approvalProfileId ?? reviewDialog.ApprovalProfileId;
    const nextRows = reviewRows.map((item) => ({
      ...item,
      nextStatus: item.nextStatus === "Chờ phê duyệt" ? "Đã phê duyệt" : item.nextStatus,
    }));
    const hasRevision = nextRows.some((item) => item.nextStatus === "Chỉnh sửa");
    setSubmitting(true);
    try {
      await subcontractApi.reviewApprovalProfile(approvalProfileId, {
        action: hasRevision ? "revision" : "approve",
        comment: reviewNote || null,
        items: nextRows.map((item) => ({
          approvalItemId: approvalItemIdOf(item),
          reviewStatus: item.nextStatus,
          comment: item.nextComment || null,
        })),
      });

      setReviewDialog(null);
      await onChanged?.();
    } catch (error) {
      setReviewError(error.response?.data?.message || error.message || "Không thể lưu kết quả phê duyệt");
    } finally {
      setSubmitting(false);
    }
  };

  const draftSelectionColumns = (
    canSubmit
      ? [
        ...itemDetailColumns,
        {
          id: "draftActions",
          header: "Thao tác",
          size: 90,
          Cell: ({ row }) => (
            <Box>
              <Tooltip title="Chỉnh sửa">
                <IconButton size="small" onClick={() => setDraftDialog({ scopeId: getScopeId(row.original), data: row.original })}>
                  <EditOutlined sx={{ fontSize: "13px" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => deleteDraftItem(row.original)}>
                  <DeleteOutline sx={{ fontSize: "13px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
      ]
      : itemDetailColumns
  );

  const reviewColumns = [
    ...itemDetailColumns.map(withCompareCellProps),
    {
      accessorKey: "nextStatus",
      header: "Kết quả duyệt",
      size: 135,
      Cell: ({ row, cell }) => row.original?._isOfficialCompare
        ? <Chip label="Bản chính" size="small" variant="outlined" />
        : <StatusChip value={cell.getValue()} />,
    },
    // {
    //   id: "comment",
    //   header: "Lý do / nhận xét",
    //   size: 220,
    //   Cell: ({ row }) => row.original?._isOfficialCompare ? null : (
    //     <AppTextField
    //       value={row.original.nextComment || ""}
    //       disabled={row.original.nextStatus !== "Chỉnh sửa"}
    //       onChange={(value) => updateReviewComment(approvalItemIdOf(row.original), value)}
    //     />
    //   ),
    // },
    // {
    //   id: "reviewActions",
    //   header: "Thao tác",
    //   size: 95,
    //   Cell: ({ row }) => {
    //     if (row.original?._isOfficialCompare) return null;
    //     const id = approvalItemIdOf(row.original);
    //     return (
    //       <Box>
    //         <Tooltip title="Duyệt">
    //           <IconButton size="small" color="success" onClick={() => markReviewItem(id, "Đã phê duyệt")}>
    //             <CheckCircleOutline fontSize="small" />
    //           </IconButton>
    //         </Tooltip>
    //         <Tooltip title="Không duyệt / yêu cầu chỉnh sửa">
    //           <IconButton size="small" color="warning" onClick={() => markReviewItem(id, "Chỉnh sửa")}>
    //             <CloseOutlined fontSize="small" />
    //           </IconButton>
    //         </Tooltip>
    //       </Box>
    //     );
    //   },
    // },
  ];

  const markAllReviewItems = (nextStatus) => {
    setReviewRows((current) => current.map((item) => ({ ...item, nextStatus })));
    showExportNotice(`Đã chuyển trạng thái các dự toán sang ${nextStatus}.`, "success");
  };

  const reviewSteps = getApprovalSteps(reviewDialog);
  const reviewCurrentStep = reviewSteps.find((step) =>
    Number(step.stepOrder ?? step.StepOrder) === Number(reviewDialog?.currentStepOrder ?? reviewDialog?.CurrentStepOrder)
  ) || reviewSteps.find((step) => {
    const status = normalize(step.status ?? step.Status);
    return status.includes("Chờ") || status.includes("PENDING") || status.includes("WAITING");
  }) || reviewSteps[reviewSteps.length - 1];
  const reviewPhaseTitle = reviewDialog
    ? (reviewDialog.respondedAt || reviewDialog.RespondedAt
      ? "Hoàn tất phê duyệt"
      : (reviewCurrentStep?.stepName || reviewCurrentStep?.StepName || reviewDialog.status || reviewDialog.Status || "Phê duyệt"))
    : "Phê duyệt";
  const reviewOverviewFields = [
    // ["Quyết định giao khoán", `${displayValue(subcontract?.subcontractNumber ?? subcontract?.SubcontractNumber)} - ${displayValue(subcontract?.subcontractName ?? subcontract?.SubcontractName)}`],
    // ["Tiêu đề phê duyệt", reviewDialog?.approvalName ?? reviewDialog?.ApprovalName],
    ["Lần phê duyệt", reviewDialog?.submitCount ?? reviewDialog?.SubmitCount],
    ["Người gửi", userLabel(userById.get(String(reviewDialog?.submittedUserId ?? reviewDialog?.SubmittedUserId))) || reviewDialog?.createBy || reviewDialog?.CreateBy],
    ["Ngày gửi", reviewDialog?.submittedAt || reviewDialog?.SubmittedAt ? formatDate(reviewDialog?.submittedAt ?? reviewDialog?.SubmittedAt) : "-"],
    ["Người phê duyệt hiện tại", userLabel(userById.get(String(reviewCurrentStep?.approverUserId ?? reviewCurrentStep?.ApproverUserId))) || reviewCurrentStep?.fullName || reviewCurrentStep?.FullName],
    ["Trạng thái hồ sơ", reviewDialog?.status ?? reviewDialog?.Status],
    ["Ngày phản hồi", reviewDialog?.respondedAt || reviewDialog?.RespondedAt ? formatDate(reviewDialog?.respondedAt ?? reviewDialog?.RespondedAt) : "-"],
  ];
  const assignmentDecisionAction = (
    <AppSplitButton
      label="Export tài liệu"
      startIcon={<FileDownloadOutlined />}
      disabled={!canCreateAssignmentDecision || submitting || assignmentFormExporting}
      onClick={openAssignmentDecisionDialog}
      menuLabel="Chọn loại báo cáo"
      size="small"
      options={[
        {
          value: "assignment-decision",
          label: "Quyết định giao khoán",
          icon: <PictureAsPdfOutlined fontSize="small" />,
          disabled: !canCreateAssignmentDecision,
          onClick: openAssignmentDecisionDialog,
        },
        {
          value: "assignment-form",
          label: "Phiếu giao khoán",
          icon: <AssignmentOutlined fontSize="small" />,
          disabled: !canCreateAssignmentDecision || assignmentFormExporting,
          onClick: downloadAssignmentFormExcel,
        },
      ]}
    />
  );
  if (canApprove) {
    return (
      <>
        <ApprovalWorkflowDialogs
          exportNotice={exportNotice}
          onCloseExportNotice={() => setExportNotice((current) => ({ ...current, open: false }))}
          decisionDialogOpen={decisionDialogOpen}
          decisionForm={decisionForm}
          decisionTeamOptions={decisionTeamOptions}
          decisionError={decisionError}
          decisionLoading={decisionLoading}
          decisionDownloading={Boolean(decisionDownloadFormat)}
          decisionDownloadFormat={decisionDownloadFormat}
          onCloseDecisionDialog={() => setDecisionDialogOpen(false)}
          onDownloadAssignmentDecisionPdf={downloadAssignmentDecisionPdf}
          onDownloadAssignmentDecisionWord={downloadAssignmentDecisionWord}
        />
        {!reviewDialog ? (
          <>
            <SectionCard title={"Thực hiện phê duyệt"}>
              <ApprovalTimeline subcontract={subcontract} approvals={approvalProfiles} />
              <Alert severity="info">Chưa có hồ sơ phê duyệt để thực hiện.</Alert>
            </SectionCard>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                minHeight: "28px",
                padding: "6px 24px",
              }}
            >
              <Typography fontWeight={700} marginBottom={"10px"}>Thực hiện phê duyệt</Typography>
              <Box>
                {assignmentDecisionAction}
              </Box>
            </Box>
            {reviewError && <Alert severity="error">{reviewError}</Alert>}
            <ApprovalTimeline subcontract={subcontract} approvals={[reviewDialog]} />
            <SectionCard title={"Thông tin tổng quan"}>
              {/* <Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, pb: 2 }}> */}
              <Box sx={{ gap: 1.5, mb: 1.5 }}>
                <Typography fontSize={13} color="text.secondary" sx={{ mb: 0.5 }}>Quyết định giao khoán</Typography>
                <Typography fontSize={13} fontWeight={650} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{displayValue(subcontract?.subcontractNumber ?? subcontract?.SubcontractNumber)} - {displayValue(subcontract?.subcontractName ?? subcontract?.SubcontractName)}</Typography>
              </Box>
              <Box sx={{ gap: 1.5, mb: 1.5 }}>
                <Typography fontSize={13} color="text.secondary" sx={{ mb: 0.5 }}>Tiêu đề phê duyệt</Typography>
                <Typography fontSize={13} fontWeight={650} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{displayValue(subcontract?.subcontractName ?? subcontract?.SubcontractName)}</Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {reviewOverviewFields.map(([label, value]) => (
                  <Box key={label} sx={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr)", gap: 1 }}>
                    <Typography fontSize={13} color="text.secondary">{label}</Typography>
                    {label === "Trạng thái hồ sơ" && value ? (
                      <Box
                        sx={{ width: "200px" }}>
                        <StatusChip value={value} />
                      </Box>
                    ) : (
                      <Typography fontSize={13} fontWeight={650} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {displayValue(value)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography fontSize={13} color="text.secondary" sx={{ mb: 0.5 }}>Nội dung / nhận xét hồ sơ</Typography>
                <Typography fontSize={13}>{displayValue(reviewDialog?.overallComment ?? reviewDialog?.OverallComment)}</Typography>
              </Box>
              {/* </Box> */}
            </SectionCard>

            <SectionCard
              title={"Thông tin chi tiết"}
              subtitle={"Chi tiết dự toán được tách theo từng phạm vi giao khoán. Các thao tác bên dưới chỉ được lưu khi bấm xác nhận phê duyệt."}
              action={(
                <FormControlLabel
                  control={<Switch checked={showOfficialCompare} onChange={(event) => setShowOfficialCompare(event.target.checked)} />}
                  label="So với bản chính"
                />
              )}
              sx={{ marginTop: "16px" }}
            >
              {reviewDisplayItemGroups.length === 0 ? (
                <Alert severity="info">Chưa có chi tiết phê duyệt.</Alert>
              ) : reviewDisplayItemGroups.map((group) => (
                <Box key={group.key} sx={{ mb: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontWeight={750}>{group.label}</Typography>
                    {group.subtitle && (
                      <Typography sx={{ fontSize: "12px", fontStyle: "italic" }} color="text.secondary">
                        {group.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <DataTable
                    data={group.items || []}
                    initialState={{
                      columnVisibility: {
                        priority: false,
                        status: false
                      },
                    }}
                    onExpandAll={() => handleExpandItemRows(group.items || [])}
                    onCollapseAll={() => handleCollapseItemRows(group.items || [])}
                    columns={reviewColumns}
                    // enableExpanding
                    minTableWidth={1950} />
                </Box>
              ))}
            </SectionCard>

            <SectionCard
              title={"Quyết định phê duyệt"}
              // subtitle={"Có thể chọn nhanh toàn bộ, sau đó điều chỉnh từng dòng trước khi xác nhận."}
              sx={{ marginTop: "16px" }}
            >
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                <AppButton variant="outlined" size="small" startIcon={<CheckCircleOutline />} onClick={() => markAllReviewItems("Đã phê duyệt")}>Duyệt tất cả</AppButton>
                <AppButton variant="outlined" size="small" color="inherit" startIcon={<EditNoteOutlined />} onClick={() => markAllReviewItems("Chỉnh sửa")}>Yêu cầu sửa tất cả</AppButton>
              </Box>
              <ReviewNoteField resetKey={reviewNoteResetKey} onValueChange={updateReviewNoteRef} />

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, paddingTop: 1 }}>
                <AppButton variant="contained" onClick={() => saveReview()} disabled={submitting}>Xác nhận phê duyệt</AppButton>
              </Box>
            </SectionCard>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <ApprovalWorkflowDialogs
        exportNotice={exportNotice}
        onCloseExportNotice={() => setExportNotice((current) => ({ ...current, open: false }))}
        decisionDialogOpen={decisionDialogOpen}
        decisionForm={decisionForm}
        decisionTeamOptions={decisionTeamOptions}
        decisionError={decisionError}
        decisionLoading={decisionLoading}
        decisionDownloading={Boolean(decisionDownloadFormat)}
        decisionDownloadFormat={decisionDownloadFormat}
        onCloseDecisionDialog={() => setDecisionDialogOpen(false)}
        onDownloadAssignmentDecisionPdf={downloadAssignmentDecisionPdf}
        onDownloadAssignmentDecisionWord={downloadAssignmentDecisionWord}
        includeApprovalDialogs
        includeReviewDialogs
        submitDialogOpen={open}
        onCloseSubmitDialog={() => setOpen(false)}
        approvalDialogTitle={approvalDialogTitle}
        approvalActionLabel={approvalActionLabel}
        approvalSubmitInitialForm={approvalSubmitInitialForm}
        isEditingCurrentApproval={isEditingCurrentApproval}
        sections={sections}
        items={items}
        userOptions={userOptions}
        submitting={submitting}
        draftNotice={draftNotice}
        draftSelectionColumns={draftSelectionColumns}
        expandedItemIds={expandedItemIds}
        onExpandItemRows={handleExpandItemRows}
        onCollapseItemRows={handleCollapseItemRows}
        canSubmit={canSubmit}
        approvalSteps={APPROVAL_STEPS}
        groupApprovalItemsByScope={groupApprovalItemsByScope}
        onAddDraftItem={(payload) => setDraftDialog(payload)}
        onSubmitApproval={submitApproval}
        approvalInfoDialog={approvalInfoDialog}
        onCloseApprovalInfoDialog={() => setApprovalInfoDialog(null)}
        approvalInfoInitialData={approvalInfoInitialData}
        onSaveApprovalInfo={saveApprovalInfo}
        approvalInfoFields={approvalInfoFields}
        draftDialog={draftDialog}
        onCloseDraftDialog={() => setDraftDialog(null)}
        onSaveDraftItem={saveDraftItem}
        draftParentOptions={draftParentOptions}
        reviewDialog={reviewDialog}
        onCloseReviewDialog={() => setReviewDialog(null)}
        reviewPhaseTitle={reviewPhaseTitle}
        reviewError={reviewError}
        subcontract={subcontract}
        reviewOverviewFields={reviewOverviewFields}
        showOfficialCompare={showOfficialCompare}
        onToggleOfficialCompare={(event) => setShowOfficialCompare(event.target.checked)}
        reviewDisplayItemGroups={reviewDisplayItemGroups}
        reviewColumns={reviewColumns}
        markAllReviewItems={markAllReviewItems}
        reviewNoteResetKey={reviewNoteResetKey}
        updateReviewNoteRef={updateReviewNoteRef}
        onSaveReview={saveReview}
        sortedReviewDisplayRows={sortedReviewDisplayRows}
      />
      <ApprovalTimeline subcontract={subcontract} approvals={approvalProfiles} />
      <SectionCard
        title="Thông tin phê duyệt"
        action={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: "28px",
            }}
          >
            {assignmentDecisionAction}
            {canSubmit ? <AppButton variant="contained" onClick={() => setOpen(true)}>{approvalActionLabel}</AppButton> : null}
          </Box>
        }
      >
        <DataTable
          columns={columns}
          data={approvalProfiles}
          // enableExpanding
          enableRowActions={canApprove || canEditApprovalInfo}
          renderRowActions={({ row }) => (
            <>
              {canEditApprovalInfo &&
                getProfileId(row.original) === editableApprovalProfileId &&
                isEditingApprovalStatus(row.original.status ?? row.original.Status) && (
                  <Tooltip title="Chỉnh sửa thông tin phê duyệt">
                    <IconButton size="small" color="primary" onClick={() => setApprovalInfoDialog(row.original)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              {canApprove ? (
                <Tooltip title="Thực hiện phê duyệt">
                  <IconButton size="small" color="primary" onClick={() => openReviewDialog(row.original)}>
                    <AssignmentTurnedInOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          )}
          renderDetailPanel={({ row }) => (
            <Box sx={{ p: 2, bgcolor: "#f8fafc" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <VisibilityOutlined fontSize="small" />
                <strong>Chi tiết {row.original.approvalName}</strong>
              </Box>
              {groupApprovalItemsByScope(row.original.items || []).length === 0 ? (
                <Alert severity="info">Chưa có chi tiết phê duyệt.</Alert>
              ) : groupApprovalItemsByScope(row.original.items || []).map((group) => (
                <Box key={group.key} sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontWeight={750}>{group.label}</Typography>
                    {group.subtitle && (
                      <Typography sx={{ fontSize: "12px", fontStyle: "italic" }} color="text.secondary">
                        {group.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <DataTable
                    columns={detailColumns}
                    data={getVisibleItemRows(group.items || [])}
                    // enableExpanding
                    onExpandAll={() => handleExpandItemRows(group.items || [])}
                    onCollapseAll={() => handleCollapseItemRows(group.items || [])}
                    initialState={{
                      columnVisibility: {
                        priority: false,
                        status: false
                      },
                    }}
                    minTableWidth={1900} />
                </Box>
              ))}
            </Box>
          )}
        />
      </SectionCard>

    </>
  );
}
